import React, { useState, useEffect } from 'react';
import api from '../../services/api';

/**
 * "Share my summaries" — Partner Link, Jenn 2026-10-07. Members who joined
 * through a practitioner's Partner Link can choose to have their weekly
 * Compass summary and quarterly check-in results cc'd to an email they pick.
 * Only shown to those members (or anyone who already has it on). The consent
 * points come from the server, so the words shown are exactly the words saved
 * with their choice. Turning it off is the same box, in the same place.
 */
export default function SummaryShareSettings() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/summary-share').then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data?.eligible) return null;

  const turnOn = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const r = await api.post('/summary-share', { email, agree });
      setData({ ...data, active: r.data.active });
      setEmail('');
      setAgree(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this. Please try again.');
    }
    setSaving(false);
  };

  const turnOff = async () => {
    setError('');
    setSaving(true);
    try {
      await api.delete('/summary-share');
      setData({ ...data, active: null });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not turn this off. Please try again.');
    }
    setSaving(false);
  };

  // "Let my professional see my name" on its own (Jenn, 2026-10-09).
  const who = data.professionalName || 'the professional who shared 365reset with you';
  const toggleName = async () => {
    setError('');
    setSaving(true);
    try {
      const r = data.nameShare ? await api.delete('/summary-share/name') : await api.post('/summary-share/name');
      setData({ ...data, nameShare: r.data.nameShare });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this. Please try again.');
    }
    setSaving(false);
  };
  const nameBox = (
    <div className="mb-6 pb-6 border-b border-gray-100">
      <h3 className="text-base font-bold text-reset-brown">Let {who} see my name</h3>
      <p className="text-sm text-gray-500 mt-0.5 mb-3">
        On their partner dashboard, {who} only sees that someone joined through their link. If you'd like, they can see your name instead.
        Nothing else is shared. This is up to you, and you can turn it off any time.
      </p>
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={!!data.nameShare || !!data.active} disabled={saving || !!data.active} onChange={toggleName}
          className="mt-1 h-5 w-5 accent-reset-terracotta" />
        <span className="text-sm text-reset-brown">
          {data.active
            ? 'Your name is already shown, because you share your summaries below.'
            : data.nameShare ? <>Your name is <strong>shown</strong> to {who}. Untick to hide it.</> : `Show my name to ${who}.`}
        </span>
      </label>
    </div>
  );

  const since = data.active?.since
    ? new Date(data.active.since).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      {nameBox}
      <h2 className="text-lg font-bold text-reset-brown">Share my summaries</h2>
      <p className="text-sm text-gray-500 mt-0.5 mb-5">
        You can choose to send a copy of your <strong>weekly Compass summary</strong> and your{' '}
        <strong>quarterly check-in results</strong> to someone you trust, like your therapist or practitioner.
        Your daily reminders are never sent. This is up to you, and you can say no.
      </p>

      {data.active ? (
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked
              disabled={saving}
              onChange={turnOff}
              className="mt-1 h-5 w-5 accent-reset-terracotta"
            />
            <span className="text-sm text-reset-brown">
              Sharing is <strong>on</strong>. A copy goes to <strong>{data.active.email}</strong>
              {since ? ` (since ${since})` : ''}. <span className="text-gray-500">Untick this box to stop sharing right away.</span>
            </span>
          </label>
        </div>
      ) : (
        <form onSubmit={turnOn} className="space-y-4">
          <div>
            <label htmlFor="share-email" className="block text-sm font-medium text-reset-brown mb-1.5">
              Email to send a copy to
            </label>
            <input
              id="share-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-reset-terracotta"
            />
          </div>
          <div className="bg-reset-cream rounded-xl p-4">
            <p className="text-sm font-bold text-reset-brown mb-2">Before you say yes, please read this:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-reset-brown">
              {data.consentPoints.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agree}
              onChange={e => setAgree(e.target.checked)}
              className="mt-1 h-5 w-5 accent-reset-terracotta"
            />
            <span className="text-sm text-reset-brown">
              I have read this and I agree. I want my summaries sent to the email above.
            </span>
          </label>
          <button
            type="submit"
            disabled={saving || !agree || !email.trim()}
            className="px-5 py-2.5 bg-reset-terracotta text-white text-sm font-bold rounded-full disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Start sharing'}
          </button>
        </form>
      )}
      {error && <p className="text-sm text-reset-terracotta mt-3">{error}</p>}
    </div>
  );
}
